<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { buildUrlFromPath, signUpWithReferrerUrl } from '@@/bits/location'
import { prefetch } from '@@/bits/prefetch'
import { isRegistered } from '@@/bits/user_model'
import { defineAsyncComponent } from '@@/bits/vue'
import { useLandsideNavigationStore } from '@@/pinia/landside_navigation'
import LandsideHeaderV2 from '@@/vuecomponents/LandsideHeaderV2.vue'
import LandsideMain from '@@/vuecomponents/LandsideMain.vue'
import { useHighContrastMode } from '@@/vuecomposables/useHighContrastMode'
import { storeToRefs } from 'pinia'
import { onBeforeUnmount, onMounted, ref } from 'vue'

const isLandsideMainMounted = ref(false)
const LandsideFooterV2 = defineAsyncComponent(() => import('@@/vuecomponents/LandsideFooterV2.vue'))
const landsideNavigationStore = useLandsideNavigationStore()
const { currentUser } = storeToRefs(landsideNavigationStore)

onMounted(() => {
  window.addEventListener('popstate', landsideNavigationStore.switchPageToUrlParams)
  if (currentUser.value != null && !isRegistered(currentUser.value)) {
    prefetch('/auth/login')
    prefetch(
      isAppUsing('newSignupWelcomePage') ? '/auth/signup' : signUpWithReferrerUrl(buildUrlFromPath('/dashboard/make')),
    )
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', landsideNavigationStore.switchPageToUrlParams)
})

function mountFooter(): void {
  // We use a timeout to let the browser render the landside main first before mounting the footer
  // Set timeout as 50ms to give some time for the browser to paint
  setTimeout(() => {
    isLandsideMainMounted.value = true
  }, 50)
}

if (isAppUsing('highContrastMode')) {
  useHighContrastMode(currentUser)
}
</script>

<template>
  <div :class="['w-full', 'flex flex-col', 'bg-light-ui-100 dark:bg-dark-ui-100']">
    <LandsideHeaderV2 data-testid="landsideHeader" />
    <LandsideMain @hook:mounted="mountFooter()" />
    <LandsideFooterV2 v-if="isLandsideMainMounted" />
  </div>
</template>

<style lang="postcss">
@import '@@/styles/5/kit';

html {
  font-size: 16px;
  color-scheme: light dark;
}

body {
  @apply scrollbar-regular dark:scrollbar-regular-dark;
  @apply scrollbar-gutter-stable;
}
</style>
